import React from "react";
import {
  StyledDownArrow,
  StyledFileListWrapper,
  StyledFileManagementContainer,
  StyledFilePage,
  StyledFilterAltFillButton,
  StyledHeadline,
  StyledImportAtOnceButton,
  StyledImportButton,
  StyledMediaContainer,
  StyledOption,
  StyledSelect,
  StyledTableHeaderColumnContainer,
} from "./styled";
import { PageLevel, useConsoleState } from "providers/ConsoleStateProvider";
import { ResourceType } from "types/graphqlapi";
import { useLoadingState } from "providers/LoadingProvider";
import { ImagePresenter } from "components/common/ImagePresenter";
import { BackButton } from "components/parts/BackButton";
import { Typography } from "components/parts/Typography/Typography";
import {
  StyledTable,
  StyledTableCell,
  StyledTbody,
  StyledTbodyTd,
  StyledThead,
  StyledTheadTh,
  StyledTheadTr,
  TableColumnProps,
  StyledNoHoverTbodyTr,
} from "components/templates/Table/styled";
import { StyledNodataTypography } from "pages/InterviewManagement/InterviewList/styled";
import { usePageHeader } from "hooks/usePageHeader";
import { InterviewTypeIcon } from "components/parts/InterviewTypeIcon";
import { VideoPresenter } from "components/common/VideoPresenter";
import {
  DisplayInterviewResource,
  useInterviewResource,
} from "pages/FIleDLManagement/hook";
import { FileDLManageModal } from "pages/FIleDLManagement/FileDLManageModal";
import { Storage } from "aws-amplify";
import { formatFileSize, getFileProperties } from "utils/common";
import { CheckBox } from "components/parts/Input/CheckBox";

export type FileResourceFormValueType = {
  file?: File;
  fileName: string;
  title: string;
  description: string;
};

const FILE_TABLE_COLUMN_LIST = [
  {
    columnId: "checkbox",
    columnType: "checkbox",
    textAlign: "left",
    minWidth: "3.5em",
    width: "400px",
    text: "",
  },
  {
    columnId: "preview",
    columnType: "media",
    textAlign: "center",
    minWidth: "8em",
    width: "400px",
    text: "プレビュー",
  },
  {
    columnId: "interviewLabel",
    columnType: "typography",
    textAlign: "left",
    minWidth: "20em",
    width: "800px",
    text: "実査",
  },
  {
    columnId: "title",
    columnType: "typography",
    textAlign: "left",
    minWidth: "15em",
    width: "800px",
    text: "タイトル",
  },
  {
    columnId: "senderName",
    columnType: "typography",
    textAlign: "left",
    minWidth: "8em",
    width: "800px",
    text: "投稿者",
  },
  {
    columnId: "createdAtString",
    columnType: "typography",
    textAlign: "left",
    minWidth: "10em",
    width: "800px",
    text: "投稿日時",
  },
  {
    columnId: "typeDescription",
    columnType: "typography",
    textAlign: "center",
    minWidth: "5em",
    width: "300px",
    text: "種類",
  },
  {
    columnId: "download",
    columnType: "button",
    textAlign: "center",
    minWidth: "7em",
    width: "300px",
    text: "ダウンロード",
  },
] as TableColumnProps[];

export const FileDLManagement: React.FC = () => {
  const { setLoading } = useLoadingState();
  const { selectedSubjectId, setPageLevel, selectedSubjectType } =
    useConsoleState();
  const { pageHeader } = usePageHeader(selectedSubjectId);
  const { interviewResourceList, fetchInterviewResource, getDownloadFileName } =
    useInterviewResource();

  const monitorInterviewResourceListWithOrderByAnsId =
    interviewResourceList.map((interviewResource) => {
      const { senderMonitor, createdAt } = interviewResource;
      if (!senderMonitor || !senderMonitor.ans_id) return interviewResource;

      const order = interviewResourceList.filter(
        (f) =>
          f.senderMonitor?.ans_id === senderMonitor.ans_id &&
          f.createdAt <= createdAt
      ).length;

      return {
        ...interviewResource,
        orderByAnsId: order,
      };
    });

  const [selectedFile, setSelectedFile] =
    React.useState<DisplayInterviewResource>();

  const onClickMediaPreview = (interviewResource: DisplayInterviewResource) => {
    setSelectedFile(interviewResource);
  };

  const closeHandler = () => {
    setSelectedFile(undefined);
  };

  const downloadInterviewResource = async (
    interviewResource: DisplayInterviewResource
  ) => {
    if (interviewResource.src) {
      const res = await Storage.get(interviewResource.url, {
        download: false,
        contentDisposition: `attachment; filename*=UTF-8''${encodeURI(
          getDownloadFileName(interviewResource)
        )}`, //参考 https://qiita.com/kitar/items/166896357f50f4fe0a95
        contentType: "application/octet-stream",
      });

      const a = document.createElement("a");
      a.href = res;

      a.download = getDownloadFileName(interviewResource);

      a.click();

      a.remove();
    }
  };

  React.useEffect(() => {
    (async () => {
      setLoading(true);
      setPageLevel(PageLevel.fileAndScenarioAndMonitor);
      await fetchInterviewResource(selectedSubjectId);
      setLoading(false);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const allOption = React.useMemo(
    () =>
      ({
        id: "ALL_INTERVIEW_RESOURCE",
        label: "すべて",
      } as const),
    []
  );

  const [selectedInterviewId, setSelectedInterviewId] = React.useState<string>(
    allOption.id
  );
  const [selectVisible, setSelectVisible] = React.useState(false);
  const [checkedResourceList, setCheckedResourceList] = React.useState<
    DisplayInterviewResource[]
  >([]);

  const onClickFilterIcon = () => {
    setSelectVisible((current) => !current);
  };

  const interviewIdLabelList = monitorInterviewResourceListWithOrderByAnsId.map(
    (resource) => ({
      id: resource.interview_id,
      label: resource.interviewLabel,
    })
  );

  const interviewFilterOptionList = [
    allOption,
    ...Array.from(
      new Map(
        interviewIdLabelList.map((interviewIdLabel) => [
          interviewIdLabel.id, // idが重複しているものを削除
          interviewIdLabel,
        ])
      ).values()
    ),
  ];

  const selectSize = Math.min(interviewFilterOptionList.length, 8); //  9個以上はスクロール

  const onChangeInterviewFilter: React.ComponentProps<"select">["onChange"] = (
    e
  ) => {
    setSelectedInterviewId(e.target.value);
    setSelectVisible(false);
    setCheckedResourceList([]);
  };

  const displayingResourceList = React.useMemo(
    () =>
      monitorInterviewResourceListWithOrderByAnsId.filter((resource) => {
        if (selectedInterviewId === allOption.id) {
          return resource;
        } else return resource.interview_id === selectedInterviewId;
      }),
    [
      allOption,
      selectedInterviewId,
      monitorInterviewResourceListWithOrderByAnsId,
    ]
  );

  const checkALLResources = () => {
    setCheckedResourceList((current) => {
      if (current.length === displayingResourceList.length) {
        return [];
      } else return displayingResourceList;
    });
  };

  const onChangeCheckResource = (resource: DisplayInterviewResource) => {
    setCheckedResourceList((current) => {
      if (
        current.some((checkedResource) => checkedResource.id === resource.id)
      ) {
        return current.filter(
          (checkedResource) => checkedResource.id !== resource.id
        );
      } else return [...current, resource];
    });
  };

  const onClickDownloadCheckedMedia = React.useCallback(async () => {
    if (checkedResourceList.length === 0) return;

    setLoading(true);

    const resourceWithSizeList = await Promise.all(
      checkedResourceList.map(async (resource) => {
        const resourceSize = (await getFileProperties(resource.url))
          ?.contentLength;
        return { resourceSize, ...resource };
      })
    );

    const sum = resourceWithSizeList.reduce(
      (prev, resource) => prev + (resource.resourceSize || 0),
      0
    );

    const confirmMessage = `
    ${checkedResourceList.length}個(合計${formatFileSize(
      sum
    )})のファイルをダウンロードします`;

    const confirm = window.confirm(confirmMessage);
    if (!confirm) {
      setLoading(false);
      return;
    }

    const sortBySizeList = resourceWithSizeList.sort((a, b) => {
      if (a.resourceSize && b.resourceSize) {
        if (a.resourceSize > b.resourceSize) return 1;
        if (a.resourceSize < b.resourceSize) return -1;
      }
      return 0;
    });

    for (const interviewResource of sortBySizeList) {
      if (interviewResource.src) {
        const res = await Storage.get(interviewResource.url, {
          download: true,
          contentDisposition: `attachment; filename*=UTF-8''${encodeURI(
            getDownloadFileName(interviewResource)
          )}`, //参考 https://qiita.com/kitar/items/166896357f50f4fe0a95
          contentType: "application/octet-stream",
        });

        const url = URL.createObjectURL(res.Body as Blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = getDownloadFileName(interviewResource);
        a.click();
        URL.revokeObjectURL(url);
        a.remove();
      }
    }
    setLoading(false);
  }, [checkedResourceList, setLoading, getDownloadFileName]);

  return (
    <>
      <StyledFilePage>
        <BackButton />
        <StyledFileManagementContainer>
          <StyledHeadline>
            <InterviewTypeIcon interviewType={selectedSubjectType} size={24} />
            <Typography
              text={`${pageHeader.subjectName}のファイル確認・DL`}
              size="xxlarge"
              weight={"bold"}
            />
          </StyledHeadline>
          <StyledFileListWrapper>
            <StyledTable>
              <StyledThead>
                <StyledTheadTr>
                  {FILE_TABLE_COLUMN_LIST.map((column) => {
                    // TODO: StyledTheadThとStyledTableCellは共通なため分岐の外に出す
                    if (column.columnId === "checkbox") {
                      return (
                        <StyledTheadTh
                          key={`MonitorTableHeaderColumn${column.columnId}`}
                          textAlign={column.textAlign}
                          minWidth={column.minWidth}
                          width={column.width}
                        >
                          <StyledTableCell minWidth={column.minWidth}>
                            <StyledTableHeaderColumnContainer>
                              <CheckBox
                                id={allOption.id}
                                onChange={checkALLResources}
                                checked={
                                  displayingResourceList.length > 0 &&
                                  displayingResourceList.length <=
                                    checkedResourceList.length
                                }
                              />
                              <StyledImportAtOnceButton
                                onClick={onClickDownloadCheckedMedia}
                              />
                            </StyledTableHeaderColumnContainer>
                          </StyledTableCell>
                        </StyledTheadTh>
                      );
                    }

                    if (column.columnId === "interviewLabel") {
                      return (
                        <StyledTheadTh
                          key={`MonitorTableHeaderColumn${column.columnId}`}
                          textAlign={column.textAlign}
                          minWidth={column.minWidth}
                          width={column.width}
                        >
                          <StyledTableCell minWidth={column.minWidth}>
                            <StyledTableHeaderColumnContainer>
                              <Typography text={column.text} />
                              {selectedInterviewId === allOption.id ? (
                                <StyledDownArrow onClick={onClickFilterIcon} />
                              ) : (
                                <StyledFilterAltFillButton
                                  onClick={onClickFilterIcon}
                                />
                              )}
                              {selectVisible && (
                                <StyledSelect
                                  autoFocus
                                  size={selectSize}
                                  value={selectedInterviewId}
                                  onChange={onChangeInterviewFilter}
                                >
                                  {interviewFilterOptionList.map((resource) => (
                                    <StyledOption
                                      key={resource.id}
                                      value={resource.id}
                                    >
                                      {resource.label}
                                    </StyledOption>
                                  ))}
                                </StyledSelect>
                              )}
                            </StyledTableHeaderColumnContainer>
                          </StyledTableCell>
                        </StyledTheadTh>
                      );
                    } else
                      return (
                        <StyledTheadTh
                          key={`MonitorTableHeaderColumn${column.columnId}`}
                          textAlign={column.textAlign}
                          minWidth={column.minWidth}
                          width={column.width}
                        >
                          <StyledTableCell minWidth={column.minWidth}>
                            <Typography text={column.text} />
                          </StyledTableCell>
                        </StyledTheadTh>
                      );
                  })}
                </StyledTheadTr>
              </StyledThead>
              <StyledTbody>
                {displayingResourceList.length === 0 ? (
                  <tr>
                    <td>
                      <StyledNodataTypography text="ファイルがありません" />
                    </td>
                  </tr>
                ) : (
                  displayingResourceList.map((resource) => (
                    <StyledNoHoverTbodyTr key={resource?.id}>
                      {FILE_TABLE_COLUMN_LIST.map((column) => (
                        <StyledTbodyTd
                          key={`${column.columnId}Of${resource?.id}`}
                          textAlign={column.textAlign}
                          minWidth={column.minWidth}
                          width={column.width}
                        >
                          <StyledTableCell minWidth={column.minWidth}>
                            {column.columnType === "checkbox" ? (
                              <CheckBox
                                id={resource.id}
                                onChange={() => {
                                  onChangeCheckResource(resource);
                                }}
                                checked={checkedResourceList.some(
                                  (checkedResource) =>
                                    checkedResource.id === resource.id
                                )}
                              />
                            ) : column.columnType === "typography" ? (
                              (() => {
                                const key =
                                  column.columnId as keyof typeof resource;
                                const resourceContent = resource[key] || "-";
                                if (typeof resourceContent === "string")
                                  return <Typography text={resourceContent} />;
                              })()
                            ) : column.columnType === "media" ? (
                              <StyledMediaContainer
                                onClick={() => {
                                  onClickMediaPreview(resource);
                                }}
                              >
                                {resource.type === ResourceType.IMAGE ? (
                                  <ImagePresenter
                                    src={resource.src}
                                    maxHeight={"15vh"}
                                  />
                                ) : (
                                  <VideoPresenter
                                    src={resource.src}
                                    maxHeight={"15vh"}
                                  />
                                )}
                              </StyledMediaContainer>
                            ) : column.columnId === "download" ? (
                              <StyledImportButton
                                onClick={() =>
                                  downloadInterviewResource(resource)
                                }
                              />
                            ) : (
                              ""
                            )}
                          </StyledTableCell>
                        </StyledTbodyTd>
                      ))}
                    </StyledNoHoverTbodyTr>
                  ))
                )}
              </StyledTbody>
            </StyledTable>
          </StyledFileListWrapper>
        </StyledFileManagementContainer>
      </StyledFilePage>
      {selectedFile && (
        <FileDLManageModal
          selectedFile={selectedFile}
          closeHandler={closeHandler}
        />
      )}
    </>
  );
};
